import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ApiService } from '../utils/api';
import { useToast } from '../components/Toast';
import { Plus, Tag } from 'lucide-react';

const emptyForm = {
  code: '',
  discountType: 'PERCENTAGE',
  discountValue: '',
  maxDiscountAmount: '',
  eventIds: [],
  oncePerUser: true,
  maxRedemptionsTotal: '',
  maxRedemptionsPerEvent: '',
  validFrom: '',
  validUntil: '',
  allowedEmails: '',
  active: true,
  currency: 'INR',
};

const toPayload = (form) => ({
  code: form.code,
  discountType: form.discountType,
  discountValue: Number(form.discountValue),
  maxDiscountAmount: form.maxDiscountAmount === '' ? null : Number(form.maxDiscountAmount),
  eventIds: form.eventIds,
  oncePerUser: !!form.oncePerUser,
  maxRedemptionsTotal: form.maxRedemptionsTotal === '' ? null : Number(form.maxRedemptionsTotal),
  maxRedemptionsPerEvent: form.maxRedemptionsPerEvent === '' ? null : Number(form.maxRedemptionsPerEvent),
  validFrom: form.validFrom ? (form.validFrom.length === 16 ? `${form.validFrom}:00` : form.validFrom) : null,
  validUntil: form.validUntil ? (form.validUntil.length === 16 ? `${form.validUntil}:00` : form.validUntil) : null,
  allowedEmails: (form.allowedEmails || '')
    .split(/[\n,]+/)
    .map((e) => e.trim())
    .filter(Boolean),
  active: !!form.active,
  currency: form.currency || 'INR',
});

const CouponsPage = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const { data: eventsResponse } = useQuery({
    queryKey: ['events'],
    queryFn: () => ApiService.getEvents(0, 100),
  });
  const events = Array.isArray(eventsResponse?.content) ? eventsResponse.content : [];

  const { data: couponsResponse, isLoading } = useQuery({
    queryKey: ['coupons'],
    queryFn: () => ApiService.listCoupons(0, 100),
  });
  const coupons = Array.isArray(couponsResponse?.content) ? couponsResponse.content : [];

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      editing?.id ? ApiService.updateCoupon(editing.id, payload) : ApiService.createCoupon(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['coupons']);
      setEditing(null);
      setForm(emptyForm);
      showSuccess(editing?.id ? 'Coupon updated' : 'Coupon created');
    },
    onError: (e) => showError(e?.message || 'Failed to save coupon'),
  });

  const toggleMutation = useMutation({
    mutationFn: (coupon) =>
      coupon.active ? ApiService.deactivateCoupon(coupon.id) : ApiService.activateCoupon(coupon.id),
    onSuccess: () => {
      queryClient.invalidateQueries(['coupons']);
    },
    onError: (e) => showError(e?.message || 'Failed to update coupon'),
  });

  const openEdit = (coupon) => {
    setEditing(coupon);
    setForm({
      code: coupon.code || '',
      discountType: coupon.discountType || 'PERCENTAGE',
      discountValue: coupon.discountValue ?? '',
      maxDiscountAmount: coupon.maxDiscountAmount ?? '',
      eventIds: coupon.eventIds || [],
      oncePerUser: coupon.oncePerUser !== false,
      maxRedemptionsTotal: coupon.maxRedemptionsTotal ?? '',
      maxRedemptionsPerEvent: coupon.maxRedemptionsPerEvent ?? '',
      validFrom: coupon.validFrom ? String(coupon.validFrom).slice(0, 16) : '',
      validUntil: coupon.validUntil ? String(coupon.validUntil).slice(0, 16) : '',
      allowedEmails: (coupon.allowedEmails || []).join('\n'),
      active: coupon.active !== false,
      currency: coupon.currency || 'INR',
    });
  };

  const toggleEvent = (eventId) => {
    setForm((prev) => ({
      ...prev,
      eventIds: prev.eventIds.includes(eventId)
        ? prev.eventIds.filter((id) => id !== eventId)
        : [...prev.eventIds, eventId],
    }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Tag className="w-6 h-6" /> Coupons</h1>
        <Button onClick={() => { setEditing({}); setForm(emptyForm); }}>
          <Plus className="w-4 h-4 mr-1" /> New coupon
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>{editing.id ? 'Edit coupon' : 'Create coupon'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </div>
              <div>
                <Label>Type</Label>
                <select
                  className="w-full border rounded-md h-10 px-2"
                  value={form.discountType}
                  onChange={(e) => setForm({
                    ...form,
                    discountType: e.target.value,
                    maxDiscountAmount: e.target.value === 'FIXED_PAYABLE' ? '' : form.maxDiscountAmount,
                  })}
                >
                  <option value="PERCENTAGE">Percentage off</option>
                  <option value="FLAT">Flat amount off</option>
                  <option value="FIXED_PAYABLE">Fixed amount to pay</option>
                </select>
              </div>
              <div>
                <Label>
                  {form.discountType === 'PERCENTAGE'
                    ? 'Value (%)'
                    : form.discountType === 'FIXED_PAYABLE'
                      ? 'Amount user pays (₹)'
                      : 'Amount off (₹)'}
                </Label>
                <Input type="number" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} />
                {form.discountType === 'FIXED_PAYABLE' && (
                  <p className="text-xs text-gray-500 mt-1">
                    User pays this amount regardless of the bill (never more than the original). Example: bill ₹7000, pay ₹2500.
                  </p>
                )}
              </div>
              {(form.discountType === 'PERCENTAGE' || form.discountType === 'FLAT') && (
                <div>
                  <Label>Max discount / upto (₹)</Label>
                  <Input
                    type="number"
                    placeholder="Optional cap"
                    value={form.maxDiscountAmount}
                    onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Caps how much can be taken off. Example: ₹4000 bill, flat ₹5000 off, upto ₹2500 → pay ₹1500.
                  </p>
                </div>
              )}
              <div>
                <Label>Total usage limit</Label>
                <Input type="number" value={form.maxRedemptionsTotal} onChange={(e) => setForm({ ...form, maxRedemptionsTotal: e.target.value })} />
              </div>
              <div>
                <Label>Per-event usage limit</Label>
                <Input type="number" value={form.maxRedemptionsPerEvent} onChange={(e) => setForm({ ...form, maxRedemptionsPerEvent: e.target.value })} />
              </div>
              <div>
                <Label>Valid from</Label>
                <Input type="datetime-local" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
              </div>
              <div>
                <Label>Valid until</Label>
                <Input type="datetime-local" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.oncePerUser} onChange={(e) => setForm({ ...form, oncePerUser: e.target.checked })} />
              Once per user (email)
            </label>
            <div>
              <Label>Events</Label>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-auto border rounded-md p-2">
                {events.map((event) => (
                  <label key={event.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.eventIds.includes(event.id)}
                      onChange={() => toggleEvent(event.id)}
                    />
                    {event.name}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Allowed emails (optional — one per line; empty = everyone)</Label>
              <Textarea
                rows={4}
                value={form.allowedEmails}
                onChange={(e) => setForm({ ...form, allowedEmails: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => saveMutation.mutate(toPayload(form))}
                disabled={saveMutation.isPending || !form.code || !form.eventIds.length}
              >
                Save
              </Button>
              <Button variant="outline" onClick={() => { setEditing(null); setForm(emptyForm); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-4 text-sm text-gray-500">Loading…</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-3">Code</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Value</th>
                  <th className="p-3">Used</th>
                  <th className="p-3">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon.id} className="border-t">
                    <td className="p-3 font-medium">{coupon.code}</td>
                    <td className="p-3">{coupon.discountType}</td>
                    <td className="p-3">{coupon.discountValue}</td>
                    <td className="p-3">{coupon.usageCountTotal || 0}{coupon.maxRedemptionsTotal != null ? ` / ${coupon.maxRedemptionsTotal}` : ''}</td>
                    <td className="p-3">{coupon.active ? 'Active' : 'Inactive'}</td>
                    <td className="p-3 space-x-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(coupon)}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => toggleMutation.mutate(coupon)}>
                        {coupon.active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Link to={`/admin/settings/coupons/${coupon.id}`} className="text-blue-600 text-sm">Usage</Link>
                    </td>
                  </tr>
                ))}
                {coupons.length === 0 && (
                  <tr><td className="p-4 text-gray-500" colSpan={6}>No coupons yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CouponsPage;
